import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  List,
  ListItem,
  ListItemText,
  Modal,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteOutlinedIcon,
  Add as PlusOutlinedIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MedicoService from '../../service/medicoService';
import './agendaMedico.css';


const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];


const DisponibilidadMedico = ({ medico, onUpdateMedico }) => {
  const [disponibilidades, setDisponibilidades] = useState(medico?.disponibilidades || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [diaSeleccionadoModal, setDiaSeleccionadoModal] = useState('');
  const [servicioId, setServicioId] = useState('');
  const [sedeNombre, setSedeNombre] = useState('');
  const [horario, setHorario] = useState([null, null]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  useEffect(() => {
    if (medico) {
      setDisponibilidades(medico.disponibilidades || []);
    }
  }, [medico]);


  const abrirModalAlta = (dia) => {
    setDiaSeleccionadoModal(dia);
    setServicioId('');
    setSedeNombre('');
    setHorario([null, null]);
    setIsModalOpen(true);
  };


 const eliminarFranja = async (indexGlobal) => {
    const franjaAEliminar = disponibilidades[indexGlobal];
    const idMedico = medico?.id || medico?._id;
    
    try {
        const medicoActualizado = await MedicoService.eliminarDisponibilidad(
            idMedico, 
            franjaAEliminar.diaSemana, 
            franjaAEliminar.horaDesde
        );
        
        const nuevasDisponibilidades = disponibilidades.filter((_, idx) => idx !== indexGlobal);
        setDisponibilidades(nuevasDisponibilidades);
        setSnackbar({ open: true, message: 'Disponibilidad removida', severity: 'info' });
        
        if (onUpdateMedico) onUpdateMedico(medicoActualizado);
    } catch (error) {
        setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
};


  const agregarNuevaFranja = async () => {
    if (!servicioId || !sedeNombre || !horario[0] || !horario[1]) {
      setSnackbar({ open: true, message: 'Completa todos los campos', severity: 'error' });
      return;
    }


    const horaDesde = horario[0].format('HH:mm');
    const horaHasta = horario[1].format('HH:mm');
   
    const sedeElegida = medico.sedes.find(s => s.nombre === sedeNombre);
   
    let especialidadObjeto = null;
    let practicaObjeto = null;


    if (servicioId.startsWith('esp-')) {
      const nombreEsp = servicioId.replace('esp-', '');
      especialidadObjeto = medico.especialidades.find(e => e.nombre === nombreEsp);
    } else {
      const nombrePrac = servicioId.replace('prac-', '');
      practicaObjeto = medico.practicas.find(p => p.nombre === nombrePrac);
    }


    const nuevaDisponibilidad = {
      diaSemana: diaSeleccionadoModal,
      horaDesde,
      horaHasta,
      sede: sedeElegida,
      especialidad: especialidadObjeto,
      practica: practicaObjeto
    };


    const idMedico = medico?._id || medico?.id;


    try {
      const medicoActualizado = await MedicoService.agregarDisponibilidad(idMedico, nuevaDisponibilidad);


      setDisponibilidades([...disponibilidades, nuevaDisponibilidad]);
      setIsModalOpen(false);
      setSnackbar({ open: true, message: 'Disponibilidad añadida', severity: 'success' });
     
      if (onUpdateMedico) onUpdateMedico(medicoActualizado);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };


  const guardarCambiosGenerales = () => {
    const perfilActualizado = { ...medico, disponibilidades };
    onUpdateMedico(perfilActualizado);
    setSnackbar({ open: true, message: 'Cambios guardados correctamente', severity: 'success' });
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="medico-config-containers">
        <div className="medico-config-headers">
          <div>
            <Typography variant="h5">Agenda y Disponibilidad Horaria</Typography>
            <Typography variant="body2" className="subtitulo-header">
              Configurá los rangos de atención de tus prácticas y especialidades.
            </Typography>
          </div>
          <Button
            variant="contained"
            className="custom-button"
            onClick={guardarCambiosGenerales}
          >
            Guardar Cambios
          </Button>
        </div>


        {DIAS_SEMANA.map((dia, indexDia) => {
          const franjasDelDia = disponibilidades
            .map((disp, idxOriginal) => ({ ...disp, idxOriginal }))
            .filter(disp => disp.diaSemana?.toUpperCase() === dia.toUpperCase());


          return (
            <Accordion key={indexDia} defaultExpanded={indexDia === 0} className="accordion-item">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className="accordion-header">
                  <Typography variant="subtitle1" className="dia-semana-text">
                    {dia}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlusOutlinedIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirModalAlta(dia);
                    }}
                    className="add-button"
                  >
                    Añadir disponibilidad
                  </Button>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {franjasDelDia.length === 0 ? (
                  <Typography className="empty-text">
                    No tienes franjas horarias configuradas para este día.
                  </Typography>
                ) : (
                  <List>
                    {franjasDelDia.map((item) => {
                      const servicioTexto = item.especialidad
                        ? `[Especialidad] ${item.especialidad.nombre}`
                        : `[Práctica] ${item.practica?.nombre}`;
                      return (
                        <ListItem
                          key={item.idxOriginal}
                          className="franja-item"
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() => eliminarFranja(item.idxOriginal)}
                              className="delete-button"
                            >
                              <DeleteOutlinedIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <div className="horario-container">
                                <AccessTimeIcon className="clock-icon" />
                                <Typography className="horario-text">
                                  {item.horaDesde} hs - {item.horaHasta} hs
                                </Typography>
                              </div>
                            }
                            secondary={
                              <div className="servicio-detalle">
                                <Typography variant="body2">
                                  <strong>Servicio:</strong> {servicioTexto}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Lugar:</strong> {item.sede?.nombre} ({item.sede?.direccion})
                                </Typography>
                              </div>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}


        <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="modal-container">
            <Typography variant="h6" className="modal-title">
              Añadir Disponibilidad - Día {diaSeleccionadoModal}
            </Typography>
           
            <FormControl fullWidth className="form-field">
              <InputLabel>Especialidad o Práctica a prestar</InputLabel>
              <Select
                value={servicioId}
                onChange={(e) => setServicioId(e.target.value)}
                label="Especialidad o Práctica a prestar"
              >
                <MenuItem disabled className="group-label">Tus Especialidades</MenuItem>
                {medico?.especialidades?.map(e => (
                  <MenuItem key={`esp-${e.nombre}`} value={`esp-${e.nombre}`}>
                    {e.nombre}
                  </MenuItem>
                ))}
                <MenuItem disabled className="group-label">Tus Prácticas</MenuItem>
                {medico?.practicas?.map(p => (
                  <MenuItem key={`prac-${p.nombre}`} value={`prac-${p.nombre}`}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            <FormControl fullWidth className="form-field">
              <InputLabel>Sede de Atención</InputLabel>
              <Select
                value={sedeNombre}
                onChange={(e) => setSedeNombre(e.target.value)}
                label="Sede de Atención"
              >
                {medico?.sedes?.map(s => (
                  <MenuItem key={s.nombre} value={s.nombre}>
                    {s.nombre} - {s.direccion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            <div className="time-range-container">
              <TimePicker
                label="Desde"
                value={horario[0]}
                onChange={(newValue) => setHorario([newValue, horario[1]])}
                className="time-picker"
              />
              <TimePicker
                label="Hasta"
                value={horario[1]}
                onChange={(newValue) => setHorario([horario[0], newValue])}
                className="time-picker"
              />
            </div>


            <div className="modal-actions">
              <Button onClick={() => setIsModalOpen(false)} className="cancel-button">
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={agregarNuevaFranja}
                className="custom-button"
              >
                Agregar Disponibilidad
              </Button>
            </div>
          </div>
        </Modal>


        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} className="snackbar-alert">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </LocalizationProvider>
  );
};


export default DisponibilidadMedico;
